import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useSocket } from "./../providers/SocketProvider";
import {
  normalizeTaskName,
  PROGRESS_STATUSES,
} from "./../config/progressConfigs";

const VALID_STATUSES = new Set(
  Object.values(PROGRESS_STATUSES)
);

function isValidProgressEvent(event) {
  return Boolean(
    event &&
      typeof event === "object" &&
      typeof event.task === "string" &&
      VALID_STATUSES.has(event.status)
  );
}

function createProgressItemId(event) {
  /*
   * Use progress_id when one backend task may appear
   * several times in the same request.
   */
  if (event.progress_id) {
    return String(event.progress_id);
  }

  const requestPart =
    event.request_id || "current-request";

  const taskPart = normalizeTaskName(event.task);

  return `${requestPart}:${taskPart}`;
}

export function useChatProgress() {
  const { socket } = useSocket();

  const [progressItems, setProgressItems] = useState([]);
  const [progressError, setProgressError] = useState(null);

  /*
   * A ref is used so startTracking() takes effect
   * immediately, without waiting for React state updates.
   */
  const activeRequestIdRef = useRef(null);

  const startTracking = useCallback((requestId) => {
    activeRequestIdRef.current = requestId || null;

    setProgressItems([]);
    setProgressError(null);
  }, []);

  const stopTracking = useCallback(() => {
    activeRequestIdRef.current = null;
  }, []);

  const clearProgress = useCallback(() => {
    setProgressItems([]);
    setProgressError(null);
  }, []);

  useEffect(() => {
    function handleChatProgress(event) {

      if (!isValidProgressEvent(event)) {
        console.warn(
          "Invalid progress event received:",
          event
        );
        return;
      }

      const activeRequestId =
        activeRequestIdRef.current;

      /*
       * Ignore messages belonging to another request.
       * Events without request_id are accepted for backward
       * compatibility with your current backend.
       */
      if (
        activeRequestId &&
        event.request_id &&
        event.request_id !== activeRequestId
      ) {
        return;
      }

      const itemId = createProgressItemId(event);

      const nextItem = {
        ...event,
        id: itemId,
      };

      setProgressItems((currentItems) => {
        const existingIndex = currentItems.findIndex(
          (item) => item.id === itemId
        );

        if (existingIndex === -1) {
          return [...currentItems, nextItem];
        }

        return currentItems.map((item, index) =>
          index === existingIndex
            ? {
                ...item,
                ...nextItem,
              }
            : item
        );
      });
    }

    function handleChatError(event) {
      const activeRequestId =
        activeRequestIdRef.current;

      if (
        activeRequestId &&
        event?.request_id &&
        event.request_id !== activeRequestId
      ) {
        return;
      }

      setProgressError(
        event?.error ||
          "هنگام پردازش درخواست خطایی رخ داد."
      );
    }

    socket.on("chat_progress", handleChatProgress);
    socket.on("chat_error", handleChatError);

    return () => {
      socket.off(
        "chat_progress",
        handleChatProgress
      );
      socket.off("chat_error", handleChatError);
    };
  }, [socket]);

  return {
    progressItems,
    progressError,
    startTracking,
    stopTracking,
    clearProgress,
  };
}